// @ts-ignore - lib/billing is plain JS
import { CHECKOUT_PRICING } from '../../lib/billing/config';

// Content for the welcome tour. Structure mirrors the approved prototype
// (docs/tc-tour.html); the data is real Traders Connect pricing and features,
// sourced from react-marketing-site/src/config/checkout.js.

// The welcome screen: the landing page's motto, then the platform's breadth.
// No invented metrics here — capabilities are facts, numbers are not.
export const WELCOME = {
  eyebrow: 'Welcome',
  // The brand line leads; the mechanic explains it underneath.
  title: 'One intelligent platform for your trading journey.',
  lead: 'Trade once and every account follows — six CFD platforms, four futures platforms, one copier across all of them.'
} as const;

// Product documentation, surfaced on the relevant tour pages.
export const DOCS = {
  accounts: 'https://docs.tradersconnect.com/category/trading-accounts',
  copier: 'https://docs.tradersconnect.com/category/trade-copier/',
  riskTypes: 'https://docs.tradersconnect.com/trade-copier/risk-types',
  copierOptions: 'https://docs.tradersconnect.com/trade-copier/copier-options',
  billing: 'https://docs.tradersconnect.com/account-management/billing',
  discord: 'https://discord.gg/tradersconnect'
} as const;

// Only claims we can stand behind. The old PLACEHOLDER_STATS carried invented
// member counts and reply times — anything shown to a user here must be true.
export const STATS = {
  medianLatencyMs: 20,
  trialLabel: '10 days'
} as const;

// A feature line on a plan card. `strong` lifts the row to full opacity and
// heavier weight — the two or three reasons someone picks this plan over the
// next one. Mirrors the `highlight` flag on the marketing pricing page.
// `featured` is optional on most cards, so both arrays are typed explicitly —
// left to inference, adding a card without the key changes the array's element
// type and silently breaks `card.featured` at the call site.
// --- Plans ------------------------------------------------------------------
// Prices, badges, blurbs and feature lists mirror the public pricing page so the
// two cannot drift. Sources, all verified against the marketing repo:
//   react-marketing-site/src/config/checkout.js — CHECKOUT_PRICING, ADDONS
//   .../LandingNew/PricingSection/PricingSection.data.js — FEATURES,
//     PRODUCT_OPTION_COPY
// One panel per licence pool; a tier is a ROW, not a card — the page's job is
// picking a lane, and the quick-subscribe dialog does the detailed selling.
// --- Plan advisor -----------------------------------------------------------
// The question every new user actually has: "which licence do I need?" The
// answer turns on what they copy FROM and TO, because CFD and futures accounts
// draw from separate licence pools — a mixed setup needs one of each.
export type AssetSide = 'cfd' | 'futures';

export interface OfferLine {
  label: string;
  /** Which SKU family prices it. */
  product: 'standard' | 'premium' | 'futures';
  /** Units priced. For a bundle line the catalog's first tier decides. */
  qty: number;
  /** The flat "unlimited accounts" futures SKU. */
  variant?: 'unlimited';
  /** The whole included bundle (futures): qty follows the catalog's first tier. */
  bundle?: boolean;
  /** The user sets how many: CFD accounts (qty is the minimum), or extra futures accounts on top of the bundle. */
  counted?: 'cfd' | 'futures';
  /** Verified monthly total, used only while the catalog loads; null = wait. */
  monthlyCents: number | null;
}

export interface AdvisorOffer {
  /** The named plan, e.g. "CFD copier · 2 accounts". */
  plan: string;
  /** One sentence on what is being bought; cycle-neutral (the ticket carries the numbers). */
  detail: string;
  /** Four true things about what the plan includes. */
  includes: string[];
  logos: 'cfd' | 'futures' | 'both';
  /** Quick-subscribe product + preset the CTA opens. */
  product: string;
  preset?: { tier?: 'premium'; analyzer?: boolean; mode?: string };
  /** Risk reversal under the CTA — the buying-anxiety answer. */
  reassure: string;
  /** The offer's other variant (Premium / Unlimited) — selectable on the ticket. */
  alt?: {
    product: string;
    preset?: { tier?: 'premium'; mode?: string };
    /** Variant toggle labels. */
    baseLabel: string;
    altLabel: string;
    plan: string;
    lines: OfferLine[];
  };
  /** The receipt: how the total is built, line by line. */
  lines: OfferLine[];
}

// Support is 24/7 (verified) and one master can feed any number of followers
// (verified); platform counts match the public pricing page. Monthly figures
// were checked against the live catalog; the ticket re-prices from it.
const SUPPORT = '24/7 support on Discord and in-app chat';
const CFD_ACCOUNT = (qty: number): OfferLine => ({
  label: 'CFD account',
  product: 'standard',
  qty,
  counted: 'cfd',
  monthlyCents: 1000 * qty
});
const PREMIUM_ACCOUNT = (qty: number): OfferLine => ({
  label: 'Premium CFD account',
  product: 'premium',
  qty,
  counted: 'cfd',
  monthlyCents: 1500 * qty
});
const FUTURES_LICENCE: OfferLine = {
  label: 'Futures licence',
  product: 'futures',
  qty: 20,
  bundle: true,
  counted: 'futures',
  // The ONE source for this figure. A duplicated 3000 here quoted the bundle at
  // $30/mo while the modal quoted $20 from billing's own constant — a 50% gap,
  // visible whenever the ticket rendered before the catalog loaded.
  monthlyCents: CHECKOUT_PRICING.futures.firstAccountMonthlyCents
};
const FUTURES_UNLIMITED: OfferLine = {
  label: 'Futures licence · unlimited accounts',
  product: 'futures',
  qty: 1,
  variant: 'unlimited',
  monthlyCents: 12000
};

// Every from/to combination, keyed `${from}-${to}`. The user picks both ends
// and gets a priced offer — no matrix to interpret, no arithmetic to do.
export const ADVISOR_MATRIX: Record<string, AdvisorOffer> = {
  'cfd-cfd': {
    plan: 'CFD copier',
    detail: 'Two CFD accounts — the one you copy from, and the one it copies to.',
    includes: [
      'One master feeds as many follower accounts as you connect',
      'All six CFD platforms — MT4, MT5, cTrader, DXtrade, TradeLocker, MatchTrader',
      'Equity protection, copier templates and full logs',
      SUPPORT
    ],
    logos: 'cfd',
    product: 'cfd',
    reassure: 'Cancel anytime. Nothing is charged until you confirm on the next screen.',
    alt: {
      product: 'cfd',
      preset: { tier: 'premium' },
      baseLabel: 'Standard',
      altLabel: 'Premium',
      plan: 'CFD copier · Premium',
      lines: [PREMIUM_ACCOUNT(2)]
    },
    lines: [CFD_ACCOUNT(2)]
  },
  'futures-futures': {
    plan: 'Futures copier',
    detail: 'One futures licence covers both ends — up to 20 accounts in total.',
    includes: [
      '10-day free trial (once per account)',
      'Tradovate, Rithmic, NinjaTrader and ProjectX',
      'Contract-level mirroring, not lot sizing',
      SUPPORT
    ],
    logos: 'futures',
    product: 'futures',
    reassure: 'Cancel before the trial ends and you pay nothing.',
    alt: {
      product: 'futures',
      preset: { mode: 'unlimited' },
      baseLabel: '20 accounts',
      altLabel: 'Unlimited',
      plan: 'Futures copier · Unlimited accounts',
      lines: [FUTURES_UNLIMITED]
    },
    lines: [FUTURES_LICENCE]
  },
  'cfd-futures': {
    plan: 'CFD + futures copier',
    detail: 'One licence of each kind: a CFD account to copy from, and the futures licence it copies to.',
    includes: [
      'Copies across both markets, contract-aware on the futures side',
      'Six CFD platforms and four futures platforms',
      'Equity protection, copier templates and full logs',
      SUPPORT
    ],
    logos: 'both',
    product: 'both',
    reassure: 'One checkout covers both licences. Cancel anytime — nothing is charged until you confirm.',
    alt: {
      product: 'both',
      preset: { tier: 'premium' },
      baseLabel: 'Standard',
      altLabel: 'Premium',
      plan: 'CFD + futures copier · Premium',
      lines: [PREMIUM_ACCOUNT(1), FUTURES_LICENCE]
    },
    lines: [CFD_ACCOUNT(1), FUTURES_LICENCE]
  },
  'futures-cfd': {
    plan: 'Futures + CFD copier',
    detail: 'One licence of each kind: the futures licence you copy from, and a CFD account it copies to.',
    includes: [
      'Copies across both markets, contract-aware on the futures side',
      'Four futures platforms and six CFD platforms',
      'Equity protection, copier templates and full logs',
      SUPPORT
    ],
    logos: 'both',
    product: 'both',
    reassure: 'One checkout covers both licences. Cancel anytime — nothing is charged until you confirm.',
    alt: {
      product: 'both',
      preset: { tier: 'premium' },
      baseLabel: 'Standard',
      altLabel: 'Premium',
      plan: 'Futures + CFD copier · Premium',
      lines: [FUTURES_LICENCE, PREMIUM_ACCOUNT(1)]
    },
    lines: [FUTURES_LICENCE, CFD_ACCOUNT(1)]
  }
};

export const ASSET_SIDES: Array<{ key: AssetSide; label: string }> = [
  { key: 'cfd', label: 'CFD' },
  { key: 'futures', label: 'Futures' }
];

// The accounts step is about breadth: platforms, brokers, health — shown as
// numbers and logos, not prose. Every figure here is verifiable in-product.
export const ACCOUNTS_FACTS = {
  platforms: { cfd: 6, futures: 4 }
};

// The copier step shows a copier the way the product shows it: the link and
// its settings panel. Field names are the Copier Engine dialog's own (Risk
// Type options, Copy Stop Loss / Take Profit / Pending Orders, Reverse Trade,
// Force Minimum/Maximum Lot Size, symbol overrides, trading hours).
export const COPIER_DEMO = {
  master: { name: 'IC Markets', platform: 'MetaTrader 5' },
  slave: { name: 'Pepperstone', platform: 'MetaTrader 4' },
  riskType: 'Lot Multiplier',
  riskSetting: '50%',
  riskExample: '1.00 lot on the master opens as 0.50 lot here.',
  toggles: [
    { label: 'Copy Stop Loss', on: true },
    { label: 'Copy Take Profit', on: true },
    { label: 'Copy Pending Orders', on: true },
    { label: 'Reverse Trade', on: false },
    { label: 'Force Minimum Lot Size', on: false },
    { label: 'Force Maximum Lot Size', on: true, value: '1.00' }
  ],
  override: { from: 'EURUSD.a', to: 'EURUSD' },
  hours: 'Mon–Fri · 08:00–22:00',
  // The five sizing rules exactly as the dialog names them.
  riskTypes: ['Fixed Lot', 'Lot Multiplier', 'Balance Multiplier', 'Fixed Balance Multiplier', 'Fixed % risk']
};

// Written against the real pipeline: trade-manager writes a log entry per action
// it takes (SEND / MODIFY / CLOSE / DELETE, each with a level and timestamp),
// analytics-manager holds the master trade, the execution proxy holds the slave
// order, and account-manager joins all three on the master ticket
// (account-manager/pkg/server/copier_logs.go).
// The logs step shows the real page's anatomy instead of describing it: a
// stylized sample of the Copier Logs table (status chip, master/slave tickets,
// symbol, sizing, profit) with one row expanded into the engine's own
// timeline. Sample data is obviously illustrative — tickets and prices are
// invented; the detect->filled gap (20ms) matches STATS.medianLatencyMs.
export type LogDemoLevel = 'success' | 'warn' | 'info';

export interface LogDemoLine {
  t: string;
  level: LogDemoLevel;
  msg: string;
}

export interface LogDemoRow {
  level: Exclude<LogDemoLevel, 'info'>;
  master: string;
  slave: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  lots: string;
  profit: string;
  /** Replaces the profit cell — the row's one-line "why" (filtered trades). */
  note?: string;
  /** The engine's own timeline for this copy — what the row expands into. */
  timeline?: LogDemoLine[];
}

export const LOGS_DEMO = {
  rows: [
    {
      level: 'success',
      master: '#81920443',
      slave: '#4471205',
      symbol: 'EURUSD',
      side: 'BUY',
      lots: '0.50 → 0.25',
      profit: '+$41.20',
      timeline: [
        { t: '14:32:07.412', level: 'info', msg: 'Master BUY 0.50 EURUSD detected' },
        { t: '14:32:07.418', level: 'info', msg: 'Risk: multiplier ×0.5 → 0.25 lots' },
        { t: '14:32:07.431', level: 'info', msg: 'Broker replied — filled at 1.08441' },
        { t: '14:32:07.432', level: 'success', msg: 'Trade copied successfully' }
      ]
    },
    { level: 'success', master: '#81920443', slave: '#7130991', symbol: 'EURUSD', side: 'BUY', lots: '0.50 → 1.00', profit: '+$82.40' },
    {
      level: 'warn',
      master: '#81920502',
      slave: '—',
      symbol: 'XAUUSD',
      side: 'BUY',
      lots: '—',
      profit: '',
      note: 'Skipped — symbol filter'
    },
    {
      level: 'success',
      master: '#81920388',
      slave: '#4471190',
      symbol: 'GBPJPY',
      side: 'SELL',
      lots: '0.25 → 0.12',
      profit: '+$12.75',
      timeline: [
        { t: '15:04:51.207', level: 'info', msg: 'Master SELL 0.25 GBPJPY detected' },
        { t: '15:04:51.213', level: 'info', msg: 'Risk: multiplier ×0.5 → 0.12 lots' },
        { t: '15:04:51.226', level: 'info', msg: 'Broker replied — filled at 191.882' },
        { t: '15:04:51.227', level: 'success', msg: 'Trade copied successfully' }
      ]
    }
  ] as LogDemoRow[],
  /** What the demo types into the search box — a master ticket, the way the real page searches. */
  query: '81920388',
  // The loop, in the order it happens on screen.
  steps: ['Both ends of every copy, one row', 'Search by ticket', 'Expand for the engine\u2019s own timeline']
};

export const DISCORD_CHANNELS = [
  '#announcements: new platforms and releases',
  '#engine-status: incident and maintenance notices',
  '#setup-help: screenshots welcome, humans replying'
];
